import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelContent, PanelHeader} from '@Components/panel';
import {useVoiceVisualizer, VoiceVisualizer} from 'react-voice-visualizer';
import {PMW_COLORS_100} from '@Utils/color.util';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Button, Size, Type} from '@Components/button';
import type {UserMediaType, UserMediaVOResponse} from '@Libraries/user-media-library';
import {MultiMediaUploadSource, uploadAudios, userMediaVOToElementData} from '@Libraries/user-media-library';
import {isArray} from 'lodash';
import {POSTER_MAX_DURATION} from '@PosterWhiteboard/poster/poster.types';
import {RecordAudioButton} from '@Panels/record-audio-panel/components/record-audio-button';
import useWindowSize from '@Hooks/useWindowSize';
import {MOBILE_MODAL_WIDTH_THRESHOLD} from '@Components/modal';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {CameraAndMicrophonePermissionDeniedMessage} from '@Panels/record-image-and-video-panel/components/camera-and-microphone-permission-denied-message';
import type {ElementData} from '@Libraries/add-media-library';
import {EditorMediaEvent, GA4EventName, GA4EventParam, GA4EventParamName, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import styles from './record-audio-panel.module.scss';

export interface RecordAudioPanelProps {
  onBack?(): void;
  onClose?(): void;
  panelId: string;
  onAddSelected(item: ElementData): void;
  collectionId?: string;
  onAddSelectedCTAText?: string;
  onStartAddSelected?: () => void;
}

const WIDTH_PER_SECOND = 135;
const EXTRA_TIMELINE_VALUE = 4;
const BUFFER_MAX_AUDIO_DURATION = 2;

export default function RecordAudioPanel({
  collectionId = '',
  onAddSelectedCTAText = window.i18next.t('pmwjs_add_to_design'),
  onStartAddSelected = (): void => {},
  ...props
}: RecordAudioPanelProps): ReactElement {
  const onClose = props.onClose?.bind(null) ?? useDefaultPanelClose(props.panelId);
  const recorderControls = useVoiceVisualizer();
  const recordedDuration = useRef<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const timelineRef = useRef<HTMLDivElement>(null);
  const [showMaxDurationError, setShowMaxDurationError] = useState(false);
  const [, setDummyValue] = useState(0);
  const {windowWidth} = useWindowSize();
  const [permissionsDeniedError, setPermissionsDeniedError] = useState(false);
  const [, setRepromptForPermissions] = useState(false);

  useEffect((): void => {
    if (!isMobileVariant()) {
      return;
    }

    let mobileHeight = 464;
    if (recorderControls.isAvailableRecordedAudio || recorderControls.isProcessingRecordedAudio) {
      mobileHeight = 536;
    }
    updateOptionsPanelHeight(props.panelId, `${mobileHeight}px`);
  }, [recorderControls.isRecordingInProgress]);

  useEffect((): void => {
    if (recorderControls.audioRef.current) {
      const newScroll = recorderControls.audioRef.current.currentTime * WIDTH_PER_SECOND;

      if (containerRef.current) {
        setDummyValue(newScroll);
        containerRef.current.scrollLeft = newScroll;
      }
    }
  }, [recorderControls?.audioRef?.current?.currentTime]);

  useEffect((): void => {
    if (recorderControls.recordingTime > (POSTER_MAX_DURATION - BUFFER_MAX_AUDIO_DURATION) * 1000) {
      setShowMaxDurationError(true);
      recorderControls.stopRecording();
    }
  }, [recorderControls.recordingTime]);

  const checkPermission = async (): Promise<void> => {
    const permission = await getPermissionStates('microphone');
    if (permission === 'denied') {
      setPermissionsDeniedError(true);
    } else if (permission === 'prompt') {
      setRepromptForPermissions(true);
    }
  };

  useEffect((): void => {
    void checkPermission();
  }, []);

  useEffect(() => {
    void checkPermission();
  }, [recorderControls.isProcessingStartRecording]);

  const getPermissionStates = async (browserPermission: string): Promise<'denied' | 'granted' | 'prompt' | 'not supported'> => {
    try {
      const result = await navigator.permissions.query({name: browserPermission as PermissionName});
      return result.state;
    } catch (error) {
      return `not supported`;
    }
  };

  const isMobileVariant = (): boolean => {
    return windowWidth < MOBILE_MODAL_WIDTH_THRESHOLD;
  };

  const showBackButton = (): boolean | undefined => {
    return !!props.onBack;
  };

  const getHeader = (): ReactElement => {
    return (
      <PanelHeader
        showBackButton={showBackButton()}
        onBackButton={(): void => {
          if (props.onBack) {
            props.onBack();
          }
        }}
        onClose={onClose}
        title={window.i18next.t('pmwjs_record_audio')}
      />
    );
  };

  const showMaxDurationErrorMessage = (): ReactElement => {
    return (
      <div className={`${styles.messageContainer} flex-row-justify-center flex-items-center`}>
        <Icon icon="icon-exclamation-triangle" size={IconSize.SIZE_ICON_20} />
        <Text val={window.i18next.t('pmwjs_audio_duration_limit_reached')} size={TextSize.XSMALL} />
      </div>
    );
  };

  const isAudioRecording = (): boolean => {
    return !recorderControls.isAvailableRecordedAudio && !recorderControls.isProcessingRecordedAudio;
  };

  const getWidthForCanvas = (): number | string => {
    if (!isAudioRecording()) {
      if (recordedDuration.current) {
        return (recordedDuration.current * WIDTH_PER_SECOND) / 1000;
      }
      return isMobileVariant() ? window.innerWidth : 780;
    }
    return isMobileVariant() ? window.innerWidth : 780;
  };

  const getTimeText = (index: number): ReactElement => {
    return <Text key={index} className="content-disabled" val={`${String(index)}s`} size={TextSize.XXSMALL} />;
  };

  const getDotStructures = (key: string): ReactElement => {
    return (
      <div key={key}>
        <Text className="content-disabled" val="." size={TextSize.XXSMALL} />
      </div>
    );
  };

  const prepareTimeline = (time: number, extraTimeline = EXTRA_TIMELINE_VALUE): Array<ReactElement> => {
    const seconds = Math.floor(time + extraTimeline);
    const decimalPart = time - Math.floor(time);
    const numOfQuarters = Math.ceil(decimalPart / 0.25);

    const data: Array<ReactElement> = [];
    for (let i = 0; i < seconds; i++) {
      data.push(getTimeText(i));
      data.push(getDotStructures(`${i}.1`));
      data.push(getDotStructures(`${i}.2`));
      data.push(getDotStructures(`${i}.3`));
      data.push(getDotStructures(`${i}.4`));
    }
    data.push(getTimeText(seconds));

    for (let j = 0; j < numOfQuarters; j++) {
      data.push(getDotStructures(`${seconds}.${j}`));
    }

    return data;
  };

  const getTimeDuration = (): ReactElement | null => {
    if (!recordedDuration.current) {
      return null;
    }
    return (
      <div
        ref={timelineRef}
        style={{
          left: `calc(50% - 3px - ${containerRef?.current?.scrollLeft ?? 0}px)`,
          width: `${(recordedDuration.current / 1000 + EXTRA_TIMELINE_VALUE) * WIDTH_PER_SECOND}px`,
        }}
        className={`${styles.timeline} ${isMobileVariant() && isAudioRecorded() ? styles.mobileRecordedTimeline : ''} flex-row-justify-between flex-items-end`}
      >
        {prepareTimeline(recordedDuration.current / 1000)}
      </div>
    );
  };

  const getContent = (): ReactElement => {
    return (
      <PanelContent className={styles.container}>
        {permissionsDeniedError ? <CameraAndMicrophonePermissionDeniedMessage /> : null}
        <div
          className={`${permissionsDeniedError ? '_hidden' : ''} ${styles.seekDivider} flex-v-row ${
            isMobileVariant() && isAudioRecorded() ? styles.mobileRecordedAudioDivider : ''
          }`}
        >
          <div className={styles.dividerUpperSection} />
          <div className={styles.dividerLowerSection} />
        </div>
        {isMobileVariant() && isAudioRecorded() ? <div className={styles.floatingPlayPauseButton}>{getPlayPauseButtonForRecordedAudio()}</div> : ''}
        <div
          ref={containerRef}
          onScroll={(e): void => {
            if (!recorderControls.isPausedRecordedAudio) {
              return;
            }
            const scrollVal = e.currentTarget.scrollLeft;
            setDummyValue(scrollVal);

            if (recorderControls.audioRef.current) {
              recorderControls.audioRef.current.currentTime = scrollVal / WIDTH_PER_SECOND;
            }
          }}
          className={`${styles.scrollContainer} flexbox ${isMobileVariant() && isAudioRecorded() ? styles.recordedAudioMobileContainer : ''} ${
            isAudioRecording() ? 'flex-content-center' : ''
          } flex-align-items-center`}
        >
          {showMaxDurationError ? showMaxDurationErrorMessage() : null}
          <VoiceVisualizer
            mainContainerClassName={isAudioRecording() ? '' : styles.visualizerContainer}
            speed={1}
            isDefaultUIShown={false}
            barWidth={4}
            gap={1}
            isControlPanelShown={false}
            mainBarColor={PMW_COLORS_100.PRIMARY}
            secondaryBarColor={PMW_COLORS_100.PRIMARY}
            rounded={5}
            isProgressIndicatorShown={false}
            isProgressIndicatorTimeShown={false}
            isProgressIndicatorOnHoverShown={false}
            onlyRecording={false}
            ref={recorderControls.audioRef}
            controls={{
              ...recorderControls,
            }}
            width={getWidthForCanvas()}
            height={isMobileVariant() ? 264 : 350}
            progressIndicatorClassName={styles.indicator}
          />
        </div>
        {isAudioRecording() ? null : getTimeDuration()}
      </PanelContent>
    );
  };

  const getRecorderButton = (): ReactElement => {
    return (
      <div className="flex-center">
        <RecordAudioButton
          onClick={(): void => {
            recorderControls.startRecording();
          }}
        />
      </div>
    );
  };

  const getRecordingTimeDurationText = (): ReactElement => {
    return <Text className={isMobileVariant() ? '_full-width' : ''} val={recorderControls.formattedRecordingTime} bold size={TextSize.SMALL} />;
  };

  const getRecordingInProgressControls = (): ReactElement => {
    return (
      <div className={`flex-row-justify-between flex-items-center ${isMobileVariant() ? '' : styles.recordingInProgressFooter}`}>
        {isMobileVariant() ? getRecordingTimeDurationText() : null}
        <div className="flex-h-row flex-items-center">
          <Icon
            onClick={(): void => {
              recorderControls.togglePauseResume();
            }}
            className={isMobileVariant() ? '' : 'spacing-m-r-5'}
            icon={recorderControls.isPausedRecording ? 'icon-play-video' : 'icon-pause'}
            size={IconSize.SIZE_ICON_32}
            shape={IconShape.CIRCLE}
            type={IconType.SHADOW}
          />
          {isMobileVariant() ? null : getRecordingTimeDurationText()}
        </div>
        <div className={isMobileVariant() ? '_full-width flexbox flex-row-reverse' : ''}>
          <Button
            onClick={(): void => {
              recordedDuration.current = recorderControls.recordingTime;
              recorderControls.stopRecording();
            }}
            size={Size.SMALL}
            type={Type.SECONDARY}
            text={window.i18next.t('pmwjs_done')}
          />
        </div>
      </div>
    );
  };

  const onUploadAudio = (type: UserMediaType, data: Array<UserMediaVOResponse> | UserMediaVOResponse): void => {
    const currentPage = window.posterEditor?.whiteboard?.getCurrentPage();
    if (!currentPage) {
      return;
    }
    if (!isArray(data)) {
      const itemData = userMediaVOToElementData(type, data);
      props.onAddSelected(itemData);
    }
  };

  const addToDesign = (): void => {
    if (recorderControls.recordedBlob) {
      onStartAddSelected();
      const date = new Date();
      const dateString = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}_${date.toLocaleTimeString()}`;
      const audioFileToAdd = new File([recorderControls.recordedBlob], `Audio_Recording_${dateString}`, {
        type: recorderControls.recordedBlob.type,
      });

      trackPosterBuilderGA4Events(GA4EventName.UPLOADSMODAL_RECORDED_MEDIA_ADDED, {
        [GA4EventParamName.TYPE]: GA4EventParam.AUDIO,
      });

      if (window.posterEditor?.whiteboard) {
        trackPosterBuilderGA4Events(EditorMediaEvent.EDITOR_RECORDEDMEDIA_ADDED, {
          [GA4EventParamName.TYPE]: GA4EventParam.AUDIO,
        });
      }

      void uploadAudios([audioFileToAdd], onUploadAudio, () => {}, {
        collectionId,
        uploadSrc: MultiMediaUploadSource.UPLOAD,
      });

      onClose();
    }
  };

  const clearRecordedAudio = (): void => {
    recorderControls.clearCanvas();

    if (showMaxDurationError) {
      setShowMaxDurationError(false);
    }
  };

  const getRetakeAudioButton = (): ReactElement => {
    return (
      <Button
        onClick={() => {
          if (window.posterEditor?.whiteboard) {
            trackPosterBuilderGA4Events(EditorMediaEvent.EDITOR_RECORDEDMEDIA_RETAKE, {
              [GA4EventParamName.TYPE]: GA4EventParam.AUDIO,
            });
          }

          clearRecordedAudio();
        }}
        customClasses="spacing-m-r-2"
        size={Size.SMALL}
        type={Type.SECONDARY}
        text={window.i18next.t('pmwjs_retake_audio')}
      />
    );
  };

  const getAddToDesignButton = (): ReactElement => {
    return <Button onClick={addToDesign} size={Size.SMALL} type={Type.PRIMARY} text={onAddSelectedCTAText} />;
  };

  const getPlayPauseButtonForRecordedAudio = (): ReactElement => {
    return (
      <Icon
        onClick={(): void => {
          recorderControls.togglePauseResume();
        }}
        icon={recorderControls.isPausedRecordedAudio ? 'icon-play-video' : 'icon-pause'}
        size={IconSize.SIZE_ICON_32}
        shape={IconShape.CIRCLE}
        type={IconType.SHADOW}
      />
    );
  };

  const getRecordedFooterControls = (): ReactElement => {
    return (
      <div className={`flex-row-justify-between flex-items-center ${isMobileVariant() ? '' : styles.recordingInProgressFooter}`}>
        {isMobileVariant() ? (
          <>
            {getRetakeAudioButton()}
            {getAddToDesignButton()}
          </>
        ) : (
          <>
            {getPlayPauseButtonForRecordedAudio()}
            <div>
              {getRetakeAudioButton()}
              {getAddToDesignButton()}
            </div>
          </>
        )}
      </div>
    );
  };

  const isAudioRecorded = (): boolean => {
    return recorderControls.isAvailableRecordedAudio || recorderControls.isProcessingRecordedAudio;
  };

  const getFooterContent = (): ReactElement => {
    if (recorderControls.isAvailableRecordedAudio || recorderControls.isProcessingRecordedAudio) {
      return getRecordedFooterControls();
    }

    if (recorderControls.isRecordingInProgress) {
      return getRecordingInProgressControls();
    }

    return getRecorderButton();
  };

  const getPanelFooter = (): ReactElement | undefined => {
    if (permissionsDeniedError) {
      return undefined;
    }
    return (
      <div className={`${styles.footer} ${isMobileVariant() && isAudioRecorded() ? styles.mobileRecordedAudioFooter : ''} ${isMobileVariant() ? styles.mobileFooter : ''}`}>
        {getFooterContent()}
      </div>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} panelFooter={getPanelFooter()} />;
}
